import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  FlexDirection,
  Heading4,
  useTheme,
} from '@gousto-internal/citrus-react'

import type {
  RecipeNutrition as RecipeNutritionInfo,
  RecipeTagGroups,
} from '@library/api-menu-experience'

import css from 'routes/Menu/components/Detail/Nutrition/NutritionInfo.css'
import { NutritionDisclaimerText } from 'routes/Menu/components/Detail/NutritionDisclaimerText'
import { SectionPanel } from 'routes/Menu/components/Detail/SectionPanel'

import { TagPill } from '../Attributes/TagPill'

type RecipeNutritionProps = {
  nutrition?: RecipeNutritionInfo
  numPortions: number
  tagGroups?: RecipeTagGroups
  loading: boolean
}

export const RecipeNutrition = ({
  nutrition,
  numPortions,
  tagGroups,
  loading,
}: RecipeNutritionProps) => {
  const { theme } = useTheme()

  if (!nutrition || loading) {
    return null
  }

  return (
    <SectionPanel>
      <Box
        data-testing="recipeDetailsNutritionInfo"
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        gap={2}
      >
        <Heading4>Nutrition</Heading4>
        {!loading && (
          <Box
            alignItems={AlignItems.FlexStart}
            wrap
            data-testid="RecipeAttributesPills"
            display={Display.Flex}
          >
            {tagGroups?.nutrition?.map((tag) => (
              <TagPill key={tag.type} type={tag.type} text={tag.text} />
            ))}
          </Box>
        )}
        <table className={css.table}>
          <thead>
            <tr style={{ backgroundColor: theme.color(Color.White) }}>
              <th className={css.tablePadding}>Typical Values</th>
              <th className={css.tablePadding}>per 100g</th>
              <th className={css.tablePadding}>per portion</th>
            </tr>
          </thead>
          <tbody>
            {nutrition.list.map((item, index) => (
              <tr
                key={item.name}
                data-testid={`nutrition-row-${index}`}
                style={{
                  backgroundColor:
                    index % 2 === 0 ? theme.color(Color.NeutralGrey_50) : theme.color(Color.White),
                }}
              >
                <td
                  className={css.tablePadding}
                  style={{ whiteSpace: 'pre-wrap' }}
                  data-testid={`nutrition-name-${index}`}
                >
                  {item.name}
                </td>
                <td className={css.tablePadding}>{item.per100g}</td>
                <td className={css.tablePadding}>{item.perServing}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
      <NutritionDisclaimerText numPortions={numPortions} />
    </SectionPanel>
  )
}
